import { useMemo, useState } from 'react'
import { Dropdown } from 'primereact/dropdown'

export interface ParameterPaletteItem {
  id: number
  /** Gruplama başlığı; boşsa öğe "Diğer" başlığı altında toplanır. */
  groupName: string | null
  name: string
  description: string | null
  /** Metne eklenecek kod, ör. `${MEETING.NAME}`. */
  parameterCode: string
}

export interface ParameterPaletteProps {
  title: string
  items: ParameterPaletteItem[]
  /** Bir koda tıklandığında çağrılır; çağıran kodu imleç konumuna ekler. */
  onInsert: (code: string) => void
  /** Katalog yüklenemedi → panel devre dışı, kısa bir mesaj gösterilir. */
  errorMessage?: string
  /** Katalog boş → kısa bir mesaj (form yine kullanılabilir). */
  emptyMessage?: string
  /** Öğe başlığında gösterilecek erişilebilirlik ipucu (ör. "İçeriğe ekle"). */
  insertLabel: string
  /** Grup filtresinin erişilebilirlik etiketi (ör. "Grup filtresi"). */
  filterLabel: string
  /** Filtre seçilmemişken gösterilen metin — "tüm gruplar" anlamındadır. */
  filterAllLabel: string
}

/**
 * Paylaşılan **parametre paneli** (059 FR-021a) — salt-okunur bir katalogdan metne kod ekletir.
 *
 * Öğeler `groupName`'e göre gruplanır (giriş sırası korunur — sunucu zaten grup + kimlik düzeninde
 * döner). Bir öğeye tıklandığında `onInsert(parameterCode)` çağrılır; **imleç konumuna ekleme
 * sorumluluğu çağırandadır** — panel metin alanını tanımaz, böylece farklı formlarda yeniden
 * kullanılabilir (Reuse Mandate).
 *
 * Panelin **üstünde bir grup filtresi** vardır (2026-08-04): katalog uzadıkça grup başlıkları arasında
 * kaydırmak yerine tek bir grup seçilebilir. Filtre **yalnız görünümü** daraltır — panel yine tek
 * katalogdan beslenir, istek atmaz. Seçim yapılmadığında (varsayılan) tüm gruplar listelenir; bu yüzden
 * "Tümü" diye bir seçenek yoktur, filtre `showClear` ile temizlenir ve boş seçim placeholder ile
 * anlatılır (`null` değerli bir seçeneği PrimeReact Dropdown "boş seçim" sayıp yutardı).
 *
 * Panel **doğrulama yapmaz**: içerikteki yer tutucuların katalogda olup olmadığına bakılmaz (spec
 * Clarifications — yer tutucu çözümleme mantığı bu özellikte yazılmadığından katalog dışı bir kodun
 * gerçekten geçersiz olduğu bilinemez).
 */
export default function ParameterPalette({
  title,
  items,
  onInsert,
  errorMessage,
  emptyMessage,
  insertLabel,
  filterLabel,
  filterAllLabel,
}: Readonly<ParameterPaletteProps>) {
  const groups = useMemo(() => {
    const byGroup = new Map<string, ParameterPaletteItem[]>()
    for (const item of items) {
      const key = item.groupName ?? ''
      const bucket = byGroup.get(key)
      if (bucket) bucket.push(item)
      else byGroup.set(key, [item])
    }
    return Array.from(byGroup.entries())
  }, [items])

  /** Seçili grup adı; `null` = filtre yok (tüm gruplar). */
  const [selected, setSelected] = useState<string | null>(null)

  /**
   * Filtre seçenekleri — panelde **gerçekten bulunan** gruplar. Adsız kova (grup sert-silinmişse
   * `groupName` `null` gelir) seçenek üretmez: gösterilecek bir etiketi yoktur, dolayısıyla yalnız
   * filtresiz görünümde listelenir.
   */
  const filterOptions = useMemo(
    () => groups.filter(([name]) => name !== '').map(([name]) => ({ label: name, value: name })),
    [groups],
  )

  /**
   * Katalog yenilenip seçili grup kaybolursa filtre **kendiliğinden temizlenir**: aksi halde dropdown
   * boş görünür ve liste sessizce boşalırdı. Türetilmiş değer kullanılır — efekt + fazladan render yok.
   */
  const active =
    selected != null && filterOptions.some((o) => o.value === selected) ? selected : null

  const visibleGroups = active == null ? groups : groups.filter(([name]) => name === active)

  return (
    <aside className="ui-param-palette" aria-label={title}>
      <div className="ui-param-palette-title">{title}</div>

      {/*
        Filtre **yalnız birden çok grup varken** gösterilir: tek gruplu bir katalogda seçim yapmak
        görünümü değiştirmez, sadece yer kaplar. Hata durumunda da gösterilmez (filtrelenecek liste yok).
      */}
      {!errorMessage && filterOptions.length > 1 && (
        <Dropdown
          className="ui-param-palette-filter"
          value={active}
          options={filterOptions}
          onChange={(e) => setSelected((e.value as string | null) ?? null)}
          placeholder={filterAllLabel}
          aria-label={filterLabel}
          showClear={active != null}
          scrollHeight="20rem"
        />
      )}

      {errorMessage ? (
        <p className="ui-param-palette-note">{errorMessage}</p>
      ) : groups.length === 0 ? (
        <p className="ui-param-palette-note">{emptyMessage}</p>
      ) : (
        <div className="ui-param-palette-body">
          {visibleGroups.map(([groupName, groupItems]) => (
            <div key={groupName} className="ui-param-palette-group">
              {groupName && <div className="ui-param-palette-group-title">{groupName}</div>}
              <ul className="ui-param-palette-list">
                {groupItems.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="ui-param-palette-item"
                      onClick={() => onInsert(item.parameterCode)}
                      title={item.description ?? `${item.name} — ${insertLabel}`}
                      aria-label={`${item.name}: ${item.parameterCode} — ${insertLabel}`}
                    >
                      <span className="ui-param-palette-item-name">{item.name}</span>
                      <code className="ui-param-palette-item-code">{item.parameterCode}</code>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </aside>
  )
}
